import { useMemo, useState } from 'react'
import { Mail, MessageSquare } from 'lucide-react'

import type { AudienceEntry } from '@/types/marketing'
import { useAudience, useSetConsent } from '@/features/marketing/hooks'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

function displayName(e: AudienceEntry) {
  const name = [e.first_name, e.last_name].filter(Boolean).join(' ')
  return name || e.company_name || '—'
}

export function AudienceTab() {
  const { data: audience = [], isLoading } = useAudience()
  const consentMutation = useSetConsent()
  const [q, setQ] = useState('')

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return audience
    return audience.filter((e) =>
      [e.first_name, e.last_name, e.company_name, e.email, e.phone]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(needle)),
    )
  }, [audience, q])

  const optedIn = useMemo(
    () => ({
      email: audience.filter((e) => e.email_opt_in).length,
      sms: audience.filter((e) => e.sms_opt_in).length,
    }),
    [audience],
  )

  const toggle = (entry: AudienceEntry, channel: 'email' | 'sms') =>
    consentMutation.mutate({
      customerId: entry.customer_id,
      channel,
      opt_in: channel === 'email' ? !entry.email_opt_in : !entry.sms_opt_in,
    })

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          Channel consent per customer. Email defaults to opted in with one-click unsubscribe;
          marketing SMS requires an explicit opt-in.
        </p>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Badge variant="secondary" className="gap-1">
            <Mail className="h-3 w-3" /> {optedIn.email}/{audience.length} email
          </Badge>
          <Badge variant="secondary" className="gap-1">
            <MessageSquare className="h-3 w-3" /> {optedIn.sms}/{audience.length} SMS
          </Badge>
        </div>
      </div>

      <Input
        placeholder="Search customers…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="max-w-sm"
      />

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          No customers found.
        </div>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-44 text-right">Marketing consent</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((e) => (
                <TableRow key={e.customer_id}>
                  <TableCell>
                    <div className="font-medium">{displayName(e)}</div>
                    {e.company_name && (
                      <div className="text-xs text-muted-foreground">{e.company_name}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{e.email || '—'}</TableCell>
                  <TableCell className="text-sm">{e.phone || '—'}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {e.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant={e.email_opt_in ? 'secondary' : 'outline'}
                        disabled={consentMutation.isPending}
                        onClick={() => toggle(e, 'email')}
                      >
                        <Mail className="mr-1 h-3.5 w-3.5" />
                        {e.email_opt_in ? 'Email on' : 'Email off'}
                      </Button>
                      <Button
                        size="sm"
                        variant={e.sms_opt_in ? 'secondary' : 'outline'}
                        disabled={consentMutation.isPending}
                        onClick={() => toggle(e, 'sms')}
                      >
                        <MessageSquare className="mr-1 h-3.5 w-3.5" />
                        {e.sms_opt_in ? 'SMS on' : 'SMS off'}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
